/**
 * The shape of `company_settings.settings`: the editable-key list, the
 * sanitizer/normalizer, and the PRISTINE defaults snapshot.
 *
 * *** EXTRACTED FROM `companySettingsService.js` (at the 500-line cap). *** Pure,
 * no Supabase import, so the settings UI dirty-check can share it. The service
 * re-exports `editableCompanySettingsKeys` / `normalizeCompanySettings`.
 *
 * *** PRISTINE DEFAULTS. *** `uiStyles.branding` is a MUTABLE object:
 * CompanySettingsContext `Object.assign`s the signed-in tenant's settings onto
 * it for UI theming. Reading defaults from it would hand the PREVIOUS tenant's
 * values to `normalizeCompanySettings()` after sign-out on a shared device — and
 * `ensureTenantSettingsRow` would copy them into a brand-new tenant's row. So a
 * frozen snapshot is taken at module load (before any mutation) and every
 * default read goes through it; `resetBrandingDefaults()` puts the shared
 * object back when the session/tenant clears.
 */
import { branding as defaultBranding } from '../../uiStyles';
import {
  ESTIMATE_RESPONSES_SETTINGS_KEY,
  ESTIMATE_RESPONSE_NOTIFY_SETTINGS_KEY,
  normalizeNotifyUserIds,
} from './estimateResponseSettings';
import {
  OFFICE_TASK_NOTIFY_SETTINGS_KEY,
  normalizeOfficeTaskNotifyUserIds,
} from './officeTaskSettings';
import { resetCompanySettingsRpcCapability } from './companySettingsRpcCapability';

export const PRISTINE_BRANDING_DEFAULTS = Object.freeze({ ...defaultBranding });

/** Restore the shared mutable `branding` object to its load-time defaults
 *  (drops every key a tenant's settings added, e.g. aiEnabled / mfa*). */
export const resetBrandingDefaults = () => {
  Object.keys(defaultBranding).forEach((key) => {
    if (!(key in PRISTINE_BRANDING_DEFAULTS)) delete defaultBranding[key];
  });
  Object.assign(defaultBranding, PRISTINE_BRANDING_DEFAULTS);
  // Sign-out / tenant switch: re-probe whether update_company_settings exists.
  resetCompanySettingsRpcCapability();
};

export const editableCompanySettingsKeys = [
  'phone',
  'email',
  'address',
  'companyName',
  'companyLegalName',
  'companyLogoUrl',
  'legalFooter',
  'timezone',
  'taxState',
  'taxEnabled',
  'taxRate',
  'invoiceTheme',
  'invoiceAccentColor',
  'invoiceNetTermsDays',
  'defaultLocale',
  'aiPostJobBehavior',
  'photoRetentionDays',
  'voidNotifyDefaultSent',
  'voidNotifyDefaultPaid',
  'autoEstimateRemindersEnabled',
  'autoInvoiceRemindersEnabled',
  /* RD-EST-RESP-001. snake_case is DELIBERATE and load-bearing while every
     sibling here is camelCase: this key is read by the edge functions
     (`ESTIMATE_RESPONSES_SETTINGS_KEY` in
     supabase/functions/_shared/estimateResponseState.ts, consumed by
     estimate-response AND send-estimate-email), so the stored spelling is a
     cross-runtime contract, not a local style choice.

     It MUST live in this list. `sanitizeCompanySettings` REBUILDS the settings
     blob from these keys alone, so a key absent from it is DELETED from the
     jsonb on the next save — an admin editing the company phone number would
     silently switch the feature off for the whole tenant. */
  ESTIMATE_RESPONSES_SETTINGS_KEY,
  /* RD-EST-RESP-001 — the "also notify" list. Same must-be-listed rule and the
     same consequence: omit it and the next unrelated settings save deletes the
     tenant's chosen recipients. It is the FIRST ARRAY-VALUED key in this list,
     which is why `sanitizeCompanySettings` grew an array branch below — the old
     coercion collapsed anything that was not a string/boolean/number to the
     branding fallback, i.e. silently to `''`. */
  ESTIMATE_RESPONSE_NOTIFY_SETTINGS_KEY,
  /* RD-ROLE-006 — the Office Inbox "also notify" list. Same must-be-listed
     rule and the same array-valued coercion branch below. */
  OFFICE_TASK_NOTIFY_SETTINGS_KEY,
  /* KAN-784 — owner-editable credit limits (dollars, 2 decimals, or null =
     the server's $1,000 default). Must be listed for the same reason as the
     keys above. SQL twin: private.company_settings_allowed_keys(). */
  'credit_billing_grant_cap',
  'credit_alert_threshold',
];

const CREDIT_LIMIT_KEYS = new Set(['credit_billing_grant_cap', 'credit_alert_threshold']);

const NET_TERMS_ALLOWED = new Set([0, 15, 30, 45, 60]);

const clampNetTerms = (v) => {
  const n = parseInt(v, 10);
  return NET_TERMS_ALLOWED.has(n) ? n : 30;
};

export const sanitizeCompanySettings = (input = {}) => {
  return editableCompanySettingsKeys.reduce((accumulator, key) => {
    const fallback = PRISTINE_BRANDING_DEFAULTS[key] ?? '';
    const value = input[key];
    if (key === 'invoiceNetTermsDays') {
      accumulator[key] = clampNetTerms(value);
      return accumulator;
    }
    /* The one array-valued setting. Normalized here — not merely passed through
       — so the CANONICAL shape (uuid-only, lowercased, deduped, sorted, capped)
       is what reaches the jsonb AND what `normalizeCompanySettings` hands back
       to the form. If the two could differ, the form would read as permanently
       dirty after a save: `useBrandingHandlers` decides that with a
       `JSON.stringify` compare of the form against the saved settings. */
    if (key === ESTIMATE_RESPONSE_NOTIFY_SETTINGS_KEY) {
      accumulator[key] = normalizeNotifyUserIds(value);
      return accumulator;
    }
    if (key === OFFICE_TASK_NOTIFY_SETTINGS_KEY) {
      accumulator[key] = normalizeOfficeTaskNotifyUserIds(value);
      return accumulator;
    }
    if (CREDIT_LIMIT_KEYS.has(key)) {
      // A finite number or null (= built-in default). Never '' — the server
      // validator refuses a string for these keys.
      accumulator[key] = typeof value === 'number' && Number.isFinite(value) ? value : null;
      return accumulator;
    }
    if (key === 'photoRetentionDays') {
      // Only an explicit positive whole number is a choice; anything else = unset (keep all).
      accumulator[key] = typeof value === 'number' && Number.isFinite(value) && value >= 1 ? Math.round(value) : null;
      return accumulator;
    }
    accumulator[key] = value === null || typeof value === 'string' || typeof value === 'boolean' || typeof value === 'number' ? value : fallback;
    return accumulator;
  }, {});
};

export const normalizeCompanySettings = (input = {}) => {
  const sanitized = sanitizeCompanySettings(input);
  return {
    ...PRISTINE_BRANDING_DEFAULTS,
    ...sanitized,
    timezone: sanitized.timezone || 'UTC',
    invoiceNetTermsDays: NET_TERMS_ALLOWED.has(sanitized.invoiceNetTermsDays) ? sanitized.invoiceNetTermsDays : 30,
  };
};


/** True when two setting values are the same (arrays/objects by value). */
const sameSettingValue = (a, b) => a === b || JSON.stringify(a) === JSON.stringify(b);

/**
 * Editable keys whose value differs between two settings objects, compared
 * key-by-key in `editableCompanySettingsKeys` order AFTER normalization — so key
 * order, extra non-editable keys (aiEnabled, mfa*) and undefined-vs-default never
 * read as a difference.
 */
export const changedEditableKeys = (a, b) => {
  const na = normalizeCompanySettings(a || {});
  const nb = normalizeCompanySettings(b || {});
  return editableCompanySettingsKeys.filter((key) => !sameSettingValue(na[key], nb[key]));
};
